import React, { useState, useEffect, useCallback, useRef } from 'react';
import { deliveryDocumentImportService } from '../services';
import api from '../services/api';
import { DeliverySupplierResolver } from './DeliverySupplierResolver';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button }    from '@/components/ui/button';
import { Badge }     from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import { useToast } from '@/hooks/use-toast';
import {
  UploadCloud, Loader2, FileText, Eye, Trash2, AlertTriangle, CheckCircle2, XCircle, HelpCircle,
  Lightbulb, ChevronDown, ChevronUp, Ban, X, FlaskConical, Sparkles, ExternalLink, Plus,
} from 'lucide-react';
import { getApiError } from '@/lib/utils';
import DeliveryLineFullMasterSearch from './DeliveryLineFullMasterSearch';
import { EvidencePillRow } from './EvidencePillRow';
import { IdentityMemoryCard, IdentityIssue, identityErrorDetail, ReviewKindTag, ChoiceButtons } from './IdentityMemory';
import { useAiCompare, AiVerdictChip, AiCompareStatus, sortWithAi } from './AiCompare';
import { DnAddProductModal } from './DnAddProductModal';

const fmt = (val) => {
  if (val == null) return '\u2014';
  const n = Number(val);
  const abs = Math.abs(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${n < 0 ? '-' : ''}\u00A3${abs}`;
};

const WARNING_LABELS = {
  document_number_not_found: 'Document number could not be identified',
  document_date_not_found: 'Document date could not be identified',
  no_line_items_parsed: 'No line items could be parsed from this document',
  scanned_pdf_no_text_layer: 'This PDF is a scanned image with no selectable text — nothing could be extracted',
  unknown_layout_structured_fields_not_extracted: 'Unrecognised document layout — fields were not extracted',
  header_computed_total_mismatch: 'The printed totals do not match the sum of the extracted lines',
  header_total_not_found_for_reconciliation: 'No printed total was found to reconcile the lines against',
  non_zero_delivery_charge_on_document: 'A non-zero delivery charge line was found on this document',
};

const LINE_FLAG_LABELS = {
  split_unit_pack_ambiguity: 'Split case — partial pack, needs manual review',
  qty_unresolved: 'Quantity could not be determined',
  unit_price_qty_net_mismatch: 'Unit price \u00d7 qty does not match the printed net value',
  net_vat_gross_mismatch: 'Net + VAT does not match the printed gross value',
  unexpected_money_token_count: 'Unexpected number of money values on this line',
  pack_size_unresolved: 'Pack/measure size could not be determined — check the source page',
};

function humanizeLineFlag(code) {
  return LINE_FLAG_LABELS[code] || code;
}

function humanizeWarning(code) {
  if (WARNING_LABELS[code]) return WARNING_LABELS[code];
  const malformed = code.match(/^malformed_or_unreadable_pdf/);
  if (malformed) return 'This file could not be read as a PDF';
  const lineFlags = code.match(/^(\d+)_line\(s\)_flagged_for_review$/);
  if (lineFlags) return `${lineFlags[1]} line(s) need manual review (see highlighted rows below)`;
  const dup = code.match(/^duplicate_upload:(exact_file|same_document_number):import_(\d+)$/);
  if (dup) return `Duplicate of a previous upload (import #${dup[2]}, ${dup[1] === 'exact_file' ? 'identical file' : 'same document number'})`;
  const partialScan = code.match(/^partial_scan_pages:(.+)$/);
  if (partialScan) return `Page(s) ${partialScan[1]} are scanned images with no text layer`;
  return code;
}

const STATUS_BADGE = {
  extracted: { label: 'Extracted', className: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: CheckCircle2 },
  needs_review: { label: 'Needs Review', className: 'bg-amber-100 text-amber-800 border-amber-300', icon: AlertTriangle },
  failed: { label: 'Failed', className: 'bg-rose-100 text-rose-800 border-rose-300', icon: XCircle },
  pending: { label: 'Pending', className: 'bg-slate-100 text-slate-700 border-slate-300', icon: HelpCircle },
  rejected: { label: 'Rejected — use manual entry', className: 'bg-red-100 text-red-800 border-red-300', icon: Ban },
};

const LAYOUT_LABEL = {
  procure_wizard: 'ProcureWizard',
  matthew_clark: 'Matthew Clark',
  unknown: 'Unknown layout',
};

// A record can be cleanly 'extracted' yet still need manual review (e.g. a
// duplicate upload) — the status badge must surface that, not just the raw
// extraction_status, otherwise a duplicate looks identical to a clean import.
function effectiveStatus(it) {
  if (it.extraction_status === 'extracted' && it.needs_manual_review) return 'needs_review';
  return it.extraction_status;
}

const CANDIDATE_CLASS_STYLE = {
  exact: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  strong_suggestion: 'bg-teal-100 text-teal-800 border-teal-300',
  review_required: 'bg-amber-100 text-amber-800 border-amber-300',
  low_confidence: 'bg-slate-100 text-slate-600 border-slate-300',
  low_similarity: 'bg-slate-100 text-slate-600 border-slate-300',
  conflicting_evidence: 'bg-rose-50 text-rose-700 border-rose-300',
  unit_conflict: 'bg-rose-100 text-rose-800 border-rose-400',
};
const CANDIDATE_CLASS_LABEL = {
  conflicting_evidence: 'conflicting evidence',
  unit_conflict: 'pack/form conflict',
};

function StatusBadge({ status }) {
  const cfg = STATUS_BADGE[status] || STATUS_BADGE.pending;
  const Icon = cfg.icon;
  return (
    <Badge variant="outline" className={cfg.className} data-testid={`ddi-status-badge-${status}`}>
      <Icon className="h-3 w-3 mr-1" />{cfg.label}
    </Badge>
  );
}

/**
 * Stocktake Step 2 — Delivery Document (PDF) Import.
 *
 * Read-only extraction review only: uploads a supplier invoice/credit-note
 * PDF, shows what the deterministic (no external AI) extractor found, and
 * flags anything ambiguous for manual review. Nothing here creates/posts a
 * Delivery or Credit note, and nothing here touches the Product Master —
 * the existing PSB matcher will consume this record in a later pass.
 *
 * Each line can also show inline Product Master candidate suggestions
 * (reusing the PSB matcher's name-matching signal) AND a full
 * Product Master search box so the reviewer can explicitly pick an
 * item outside that ranked shortlist (see DeliveryLineFullMasterSearch).
 * Either path writes the SAME per-line `reviewed_product_id` via
 * PUT lines/{id}/reviewed-product and goes through the SAME
 * portfolio-wide "remembered pair" teach/conflict flow — there is no
 * separate confirm step and nothing here creates a product or posts a
 * Delivery/Credit note.
 */
export default function DeliveryDocumentImportPanel({ sessionId, isEditable }) {
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [discardTarget, setDiscardTarget] = useState(null);
  const [discardingId, setDiscardingId] = useState(null);
  const [candidatesByLine, setCandidatesByLine] = useState({});
  const [pillsByLine, setPillsByLine] = useState({});
  const [expandedLineIds, setExpandedLineIds] = useState(new Set());
  const [rememberedMatches, setRememberedMatches] = useState({});

  // Pass 6: research state per line_id  {[lineId]: {loading, sections, error}}
  const [lineResearch, setLineResearch] = useState({});
  // Pass 6: Add Product modal — {line, refetch} | null
  const [dnAddProductModal, setDnAddProductModal] = useState(null);

  const load = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const data = await deliveryDocumentImportService.list(sessionId);
      setItems(data.items || []);
    } catch (err) {
      toast({ title: 'Failed to load delivery document imports', description: getApiError(err), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [sessionId, toast]);

  useEffect(() => { load(); }, [load]);

  const scrollToManualEntry = () => {
    // Close the review dialog first — otherwise its overlay stays on top
    // of the native entry form we're scrolling to, so the user can see
    // the page move but can't actually click into the form underneath.
    setDetail(null);
    setTimeout(() => {
      document.getElementById('native-delivery-entry-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploading(true);
    try {
      const result = await deliveryDocumentImportService.upload(sessionId, file);
      setItems((prev) => [result, ...prev]);
      if (result.preflight_passed === false) {
        const desc = (result.rejection_reason_labels || []).join(' ') || 'This document could not be safely auto-reviewed.';
        toast({
          title: 'Rejected — use manual entry', description: `${file.name}: ${desc}`, variant: 'destructive',
        });
      } else if (result.needs_manual_review) {
        const desc = (result.warnings || []).map(humanizeWarning).join('; ') || 'See flagged lines below.';
        toast({ title: 'Uploaded — needs manual review', description: `${file.name}: ${desc}` });
      } else {
        toast({ title: 'Uploaded and extracted cleanly', description: `${file.name}: totals reconciled, ${result.line_count} line(s).` });
      }
    } catch (err) {
      toast({ title: 'Upload failed', description: getApiError(err), variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const loadRememberedMatches = async (importId) => {
    try {
      const data = await deliveryDocumentImportService.getRememberedMatches(importId);
      setRememberedMatches(data.matches || {});
    } catch (err) {
      // Non-critical — the "Suggest" candidates flow still works without this.
      setRememberedMatches({});
    }
  };

  const openDetail = async (id) => {
    setDetailLoading(true);
    setCandidatesByLine({});
    setPillsByLine({});
    setExpandedLineIds(new Set());
    setRememberedMatches({});
    setIdentityIssues({});
    try {
      const data = await deliveryDocumentImportService.get(id);
      setDetail(data);
      if (data.reviewed_supplier_id) {
        loadRememberedMatches(id);
      }
    } catch (err) {
      toast({ title: 'Failed to load review', description: getApiError(err), variant: 'destructive' });
    } finally {
      setDetailLoading(false);
    }
  };

  const loadPillsForLine = async (lineId, productIds) => {
    const ids = (productIds || []).filter((pid) => pillsByLine[lineId]?.[pid] === undefined);
    if (ids.length === 0) return;
    setPillsByLine((prev) => ({ ...prev, [lineId]: { ...(prev[lineId] || {}), ...Object.fromEntries(ids.map((id) => [id, { loading: true }])) } }));
    try {
      const data = await deliveryDocumentImportService.getLineEvidencePills(lineId, ids);
      setPillsByLine((prev) => ({
        ...prev,
        [lineId]: { ...(prev[lineId] || {}), ...Object.fromEntries(ids.map((id) => [id, data.pills_by_product?.[String(id)] || null])) },
      }));
    } catch (err) {
      // Non-critical display-only enhancement — candidates/search still work without
      // pills. Drop the keys (rather than caching a permanent failure) so the next
      // expand/search of this line retries instead of silently staying blank forever.
      setPillsByLine((prev) => {
        const lineState = { ...(prev[lineId] || {}) };
        ids.forEach((id) => delete lineState[id]);
        return { ...prev, [lineId]: lineState };
      });
    }
  };

  const { byKey: aiByKey, run: runAiCompare } = useAiCompare();
  // Keep the candidate panel (name, evidence, Select) within the visible width of the
  // horizontally scrollable lines table, so nothing needs sideways scrolling.
  const linesScrollRef = useRef(null);
  const [linesWidth, setLinesWidth] = useState(0);
  useEffect(() => {
    const el = linesScrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setLinesWidth(el.clientWidth - 2));
    ro.observe(el);
    return () => ro.disconnect();
  });
  const runAi = (key, lineId, ids) => runAiCompare(key, { workflow: 'dn', dn_line_id: lineId, candidate_ids: ids.slice(0, 8) });

  const toggleCandidates = async (lineId) => {
    setExpandedLineIds((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId); else next.add(lineId);
      return next;
    });
    if (candidatesByLine[lineId] && !candidatesByLine[lineId].error) {
      loadPillsForLine(lineId, (candidatesByLine[lineId].candidates || []).map((c) => c.product_id));
      return;
    }
    setCandidatesByLine((prev) => ({ ...prev, [lineId]: { loading: true } }));
    try {
      const data = await deliveryDocumentImportService.getLineCandidates(lineId);
      const candidates = data.candidates || [];
      setCandidatesByLine((prev) => ({ ...prev, [lineId]: { loading: false, candidates } }));
      loadPillsForLine(lineId, candidates.map((c) => c.product_id));
      if (candidates.length) runAi(`rank-${lineId}`, lineId, candidates.map((c) => c.product_id));
    } catch (err) {
      setCandidatesByLine((prev) => ({ ...prev, [lineId]: { loading: false, error: getApiError(err) } }));
    }
  };

  const [savingLineId, setSavingLineId] = useState(null);

  const [identityIssues, setIdentityIssues] = useState({});
  const dropIssue = (lineId) => setIdentityIssues((prev) => { const next = { ...prev }; delete next[lineId]; return next; });

  const selectReviewedProduct = async (lineId, productId, opts = {}) => {
    setSavingLineId(lineId);
    try {
      const updated = await deliveryDocumentImportService.setReviewedProduct(lineId, productId, opts);
      dropIssue(lineId);
      setDetail((prev) => (prev ? {
        ...prev,
        lines: prev.lines.map((l) => (l.id === lineId ? { ...l, reviewed_product_id: updated.reviewed_product_id, reviewed_product: updated.reviewed_product, review_kind: updated.review_kind, identity_memory_id: updated.identity_memory_id } : l)),
      } : prev));
      if (productId) {
        setExpandedLineIds((prev) => { const next = new Set(prev); next.delete(lineId); return next; });
        setRememberedMatches((prev) => { const next = { ...prev }; delete next[lineId]; return next; });
      } else if (detail?.reviewed_supplier_id) {
        // Clearing a line never revokes a remembered identity; re-check what applies now.
        loadRememberedMatches(detail.id);
      }
      if (updated.memory?.status === 'remembered' || updated.memory?.status === 'corrected') {
        toast({ title: updated.memory.status === 'corrected' ? 'Remembered identity corrected' : 'Remembered as the same product', description: 'Future documents from this supplier with the same SKU and pack will suggest it (never applied automatically).' });
      }
    } catch (err) {
      const issue = identityErrorDetail(err);
      if (issue) setIdentityIssues((prev) => ({ ...prev, [lineId]: { ...issue, productId } }));
      else toast({ title: 'Could not save selection', description: getApiError(err), variant: 'destructive' });
    } finally {
      setSavingLineId(null);
    }
  };

  const handleDiscard = async (id) => {
    setDiscardingId(id);
    try {
      await deliveryDocumentImportService.discard(id);
      setItems((prev) => prev.filter((it) => it.id !== id));
      setDetail((prev) => (prev && prev.id === id ? null : prev));
      toast({ title: 'Review record discarded' });
    } catch (err) {
      toast({ title: 'Failed to discard', description: getApiError(err), variant: 'destructive' });
    } finally {
      setDiscardingId(null);
      setDiscardTarget(null);
    }
  };

  // Pass 6: Research a DN line (AI + online evidence)
  const handleDnResearch = useCallback(async (lineId, descriptionRaw) => {
    setLineResearch(prev => ({ ...prev, [lineId]: { loading: true, sections: null, error: null } }));
    try {
      const { data } = await api.post(
        `/stocktake/delivery-documents/lines/${lineId}/research-product`
      );
      setLineResearch(prev => ({ ...prev, [lineId]: { loading: false, sections: data.sections, error: null } }));
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Research failed';
      setLineResearch(prev => ({ ...prev, [lineId]: { loading: false, sections: null, error: String(msg) } }));
    }
  }, []);

  // Pass 6: open Add Product modal for a DN line
  const handleDnOpenAddProduct = useCallback((line) => {
    setDnAddProductModal({ line });
  }, []);

  // Pass 6: render research evidence (AI + online sections)
  const renderResearchSections = (sections) => {
    if (!sections?.length) return null;
    return (
      <div className="space-y-2 mt-2" data-testid="dn-research-sections">
        {sections.map((s, i) => (
          <div key={i} className="rounded border bg-slate-50 p-2 text-xs">
            <div className="flex items-center gap-1 font-semibold text-slate-700 mb-1">
              {s.source === 'AI' ? <Sparkles className="h-3 w-3 text-indigo-500" /> : <FlaskConical className="h-3 w-3 text-emerald-600" />}
              {s.source}
              {s.from_cache && <span className="text-slate-400 italic font-normal ml-1">cached</span>}
              {!s.available && <Badge variant="outline" className="ml-1 text-[10px]">unavailable</Badge>}
            </div>
            {/* AI section: show advisory text */}
            {s.source === 'AI' && s.available && s.content && (
              <div className="text-slate-600 whitespace-pre-wrap leading-snug" data-testid="dn-research-ai-content">
                {typeof s.content === 'string' ? s.content : JSON.stringify(s.content, null, 1)}
              </div>
            )}
            {/* Online section: show structured fields */}
            {s.source !== 'AI' && s.available && s.content?.found && (
              <div className="space-y-0.5" data-testid="dn-research-online-content">
                {s.content.product_name  && <div><span className="text-slate-500">Name: </span>{s.content.product_name}</div>}
                {s.content.brand         && <div><span className="text-slate-500">Brand: </span>{s.content.brand}</div>}
                {s.content.quantity      && <div><span className="text-slate-500">Quantity: </span>{s.content.quantity}</div>}
                {s.content.barcode       && <div><span className="text-slate-500">Barcode: </span><span className="font-mono">{s.content.barcode}</span></div>}
                {s.content.categories?.length > 0 && <div><span className="text-slate-500">Categories: </span>{s.content.categories.join(', ')}</div>}
                {s.data_quality && <div className="text-slate-400 italic mt-1">{s.data_quality}</div>}
                {s.source_url && (
                  <a href={s.source_url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-0.5 text-indigo-600 hover:underline mt-1">
                    <ExternalLink className="h-3 w-3" />View source
                  </a>
                )}
              </div>
            )}
            {s.source !== 'AI' && s.available && s.content && !s.content.found && (
              <div className="text-slate-500 italic">No match found on {s.source}</div>
            )}
            {/* Error / unavailable note */}
            {s.note && <div className="text-slate-400 italic mt-1" data-testid="dn-research-note">{s.note}</div>}
            {s.error && <div className="text-rose-600 mt-1">{s.error}</div>}
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card data-testid="delivery-document-import-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="h-4 w-4" /> Import Delivery Documents (PDF)
        </CardTitle>
        <CardDescription>
          Upload a supplier invoice or credit note PDF for a read-only extraction review.
          This does NOT create a delivery/credit note or change the Product Master — review
          the extracted fields here first.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isEditable && (
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={handleFileSelect}
              data-testid="ddi-file-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="ddi-upload-button"
            >
              {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UploadCloud className="h-4 w-4 mr-2" />}
              {uploading ? 'Uploading & extracting…' : 'Upload invoice / credit note PDF'}
            </Button>
          </div>
        )}

        {loading ? (
          <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-muted-foreground" data-testid="ddi-empty-state">No delivery documents uploaded for this session yet.</div>
        ) : (
          <div className="border rounded-md overflow-x-auto">
            <Table data-testid="ddi-import-table">
              <TableHeader>
                <TableRow>
                  <TableHead>File</TableHead>
                  <TableHead>Layout</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Doc #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead className="text-right">Gross</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((it) => (
                  <TableRow key={it.id} data-testid={`ddi-row-${it.id}`}>
                    <TableCell className="max-w-[160px] truncate" title={it.original_filename}>{it.original_filename}</TableCell>
                    <TableCell>{LAYOUT_LABEL[it.layout_detected] || it.layout_detected}</TableCell>
                    <TableCell className="capitalize">{(it.document_type || 'unknown').replace('_', ' ')}</TableCell>
                    <TableCell>{it.document_number || '\u2014'}</TableCell>
                    <TableCell>{it.document_date || '\u2014'}</TableCell>
                    <TableCell className="max-w-[140px] truncate" title={it.supplier_name_raw}>{it.supplier_name_raw || '\u2014'}</TableCell>
                    <TableCell className="text-right">{it.preflight_passed === false ? '\u2014' : fmt(it.header_gross ?? it.computed_gross)}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <span title={(it.rejection_reason_labels || []).join(' ') || undefined}>
                          <StatusBadge status={effectiveStatus(it)} />
                        </span>
                        {it.is_duplicate && (
                          <Badge variant="outline" className="bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300 w-fit" data-testid={`ddi-duplicate-badge-${it.id}`}>
                            Duplicate ({it.duplicate_reason === 'exact_file' ? 'same file' : 'same doc #'})
                          </Badge>
                        )}
                        {it.totals_reconciled === false && (
                          <Badge variant="outline" className="bg-rose-100 text-rose-800 border-rose-300 w-fit">
                            Totals mismatch
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openDetail(it.id)} data-testid={`ddi-view-button-${it.id}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        {isEditable && (
                          <Button
                            size="icon" variant="ghost" onClick={() => setDiscardTarget(it)}
                            disabled={discardingId === it.id}
                            data-testid={`ddi-discard-button-${it.id}`}
                          >
                            {discardingId === it.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4 text-rose-600" />}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <AlertDialog open={!!discardTarget} onOpenChange={(open) => { if (!open) setDiscardTarget(null); }}>
        <AlertDialogContent data-testid="ddi-discard-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard this review record?</AlertDialogTitle>
            <AlertDialogDescription>
              "{discardTarget?.original_filename}" will be permanently removed from this list. This only
              discards the extraction review — it never created a delivery/credit note, so nothing else is affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ddi-discard-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => discardTarget && handleDiscard(discardTarget.id)}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="ddi-discard-confirm"
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!detail || detailLoading} onOpenChange={(open) => { if (!open) setDetail(null); }}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="ddi-detail-dialog">
          <DialogHeader>
            <DialogTitle>{detail?.original_filename || 'Loading…'}</DialogTitle>
          </DialogHeader>
          {detailLoading && !detail ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground p-6"><Loader2 className="h-4 w-4 animate-spin" /> Loading review…</div>
          ) : detail && (
            <div className="flex-1 min-h-0 overflow-y-auto pr-4" data-testid="ddi-detail-scroll-area">
              <div className="space-y-4">
                {detail.preflight_passed === false && (
                  <div className="border border-red-300 bg-red-50 rounded-md p-3 text-sm" data-testid="ddi-rejection-banner">
                    <div className="font-medium text-red-900 mb-1 flex items-center gap-1">
                      <Ban className="h-4 w-4" /> Rejected — not safe for automatic review
                    </div>
                    <ul className="list-disc list-inside text-red-800 text-xs space-y-0.5 mb-2">
                      {(detail.rejection_reason_labels || []).map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                    <p className="text-xs text-red-800 mb-2">
                      Nothing was guessed and no lines were stored for this document. Enter this delivery/credit
                      manually using the form below instead.
                    </p>
                    <Button
                      size="sm" variant="outline" className="border-red-400 text-red-900 hover:bg-red-100"
                      onClick={scrollToManualEntry} data-testid="ddi-rejection-manual-entry-cta"
                    >
                      Go to manual entry
                    </Button>
                  </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div><span className="text-muted-foreground">Layout</span><div className="font-medium">{LAYOUT_LABEL[detail.layout_detected] || detail.layout_detected}</div></div>
                  <div><span className="text-muted-foreground">Doc Type</span><div className="font-medium capitalize">{(detail.document_type || 'unknown').replace('_', ' ')}</div></div>
                  <div><span className="text-muted-foreground">Doc Number</span><div className="font-medium">{detail.document_number || '\u2014'}</div></div>
                  <div><span className="text-muted-foreground">Date</span><div className="font-medium">{detail.document_date || '\u2014'}</div></div>
                  <div><span className="text-muted-foreground">Account #</span><div className="font-medium">{detail.account_number_raw || '\u2014'}</div></div>
                  <div><span className="text-muted-foreground">Order #</span><div className="font-medium">{detail.order_number_raw || '\u2014'}</div></div>
                  <div><span className="text-muted-foreground">Pages</span><div className="font-medium">{detail.page_count}</div></div>
                </div>

                <div className="border rounded-md p-3" data-testid="ddi-supplier-field">
                  <div className="text-muted-foreground text-sm mb-1">Supplier</div>
                  {detail.preflight_passed === false ? (
                    <div className="font-medium text-sm">{detail.supplier_name_raw || '\u2014'}</div>
                  ) : (
                    <DeliverySupplierResolver
                      importId={detail.id}
                      supplierNameRaw={detail.supplier_name_raw}
                      reviewedSupplier={detail.reviewed_supplier}
                      isEditable={isEditable}
                      onChange={(supplier, supplierId) => {
                        setDetail((prev) => ({ ...prev, reviewed_supplier: supplier, reviewed_supplier_id: supplierId }));
                        if (supplierId) {
                          loadRememberedMatches(detail.id);
                        } else {
                          // Matches depend on a confirmed supplier identity —
                          // clearing it must clear any stale remembered-match
                          // chips/conflict banners, not leave them showing.
                          setRememberedMatches({});
                          setIdentityIssues({});
                        }
                      }}
                    />
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="border rounded-md p-3" data-testid="ddi-delivered-to">
                    <div className="text-muted-foreground mb-1">Delivered to</div>
                    <pre className="whitespace-pre-wrap font-sans text-xs">{detail.delivered_to_raw || '\u2014'}</pre>
                  </div>
                  <div className="border rounded-md p-3" data-testid="ddi-invoiced-to">
                    <div className="text-muted-foreground mb-1">Invoiced to</div>
                    <pre className="whitespace-pre-wrap font-sans text-xs">{detail.invoiced_to_raw || '\u2014'}</pre>
                  </div>
                </div>

                {detail.preflight_passed !== false && (
                <div className="border rounded-md p-3 flex flex-wrap gap-4 text-sm" data-testid="ddi-reconciliation">
                  <div><span className="text-muted-foreground">Header Net/VAT/Gross</span><div className="font-medium">{fmt(detail.header_net)} / {fmt(detail.header_vat)} / {fmt(detail.header_gross)}</div></div>
                  <div><span className="text-muted-foreground">Computed Net/VAT/Gross</span><div className="font-medium">{fmt(detail.computed_net)} / {fmt(detail.computed_vat)} / {fmt(detail.computed_gross)}</div></div>
                  <div>
                    <span className="text-muted-foreground">Reconciled</span>
                    <div className="font-medium">
                      {detail.totals_reconciled === true && <span className="text-emerald-700">Yes (within £{Number(detail.totals_discrepancy ?? 0).toFixed(2)})</span>}
                      {detail.totals_reconciled === false && <span className="text-rose-700">No — discrepancy £{Number(detail.totals_discrepancy ?? 0).toFixed(2)}</span>}
                      {detail.totals_reconciled == null && <span className="text-slate-500">Not determinable</span>}
                    </div>
                  </div>
                </div>
                )}

                {detail.preflight_passed !== false && (detail.warnings || []).length > 0 && (
                  <div className="border border-amber-300 bg-amber-50 rounded-md p-3 text-sm" data-testid="ddi-warnings">
                    <div className="font-medium text-amber-800 mb-1 flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> Flagged for manual review</div>
                    <ul className="list-disc list-inside text-amber-800 text-xs space-y-0.5">
                      {detail.warnings.map((w, i) => <li key={i}>{humanizeWarning(w)}</li>)}
                    </ul>
                  </div>
                )}

                {detail.pages?.some((p) => p.is_scanned) && (
                  <div className="border border-rose-300 bg-rose-50 rounded-md p-2 text-xs text-rose-800" data-testid="ddi-scanned-warning">
                    Page(s) {detail.pages.filter((p) => p.is_scanned).map((p) => p.page_number).join(', ')} have no extractable text layer (scanned image) — nothing was guessed for these pages.
                  </div>
                )}

                {detail.preflight_passed !== false && (
                <div>
                  <div className="font-medium text-sm mb-2">Lines ({detail.lines?.length || 0})</div>
                  <div className="border rounded-md overflow-x-auto" ref={linesScrollRef}>
                    <Table data-testid="ddi-lines-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>#</TableHead>
                          <TableHead>Pg</TableHead>
                          <TableHead>Code</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Size</TableHead>
                          <TableHead>Unit</TableHead>
                          <TableHead className="text-right">Qty</TableHead>
                          <TableHead className="text-right">Net</TableHead>
                          <TableHead className="text-right">VAT</TableHead>
                          <TableHead className="text-right">Gross</TableHead>
                          <TableHead>Flags</TableHead>
                          <TableHead>Product Master</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(detail.lines || []).map((l) => {
                          const expanded = expandedLineIds.has(l.id);
                          const candState = candidatesByLine[l.id];
                          return (
                          <React.Fragment key={l.id}>
                          <TableRow className={l.ambiguity_flags?.length ? 'bg-amber-50' : ''} data-testid={`ddi-line-${l.id}`}>
                            <TableCell>{l.line_sequence}</TableCell>
                            <TableCell>{l.page_number ?? '\u2014'}</TableCell>
                            <TableCell>{l.product_code_raw || '\u2014'}</TableCell>
                            <TableCell className="max-w-[220px] truncate" title={l.description_raw}>{l.description_raw}</TableCell>
                            <TableCell>{l.size_raw || '\u2014'}</TableCell>
                            <TableCell>{l.unit_raw || '\u2014'}</TableCell>
                            <TableCell className="text-right">{l.quantity ?? '\u2014'}</TableCell>
                            <TableCell className="text-right">{fmt(l.line_net)}</TableCell>
                            <TableCell className="text-right">{fmt(l.line_vat)}</TableCell>
                            <TableCell className="text-right">{fmt(l.line_gross)}</TableCell>
                            <TableCell>
                              {l.ambiguity_flags?.length > 0 ? (
                                <Badge
                                  variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]"
                                  title={l.ambiguity_flags.join(', ')} data-testid={`ddi-line-flag-${l.id}`}
                                >
                                  {l.ambiguity_flags.map(humanizeLineFlag).join('; ')}
                                </Badge>
                              ) : (
                                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                              )}
                            </TableCell>
                            <TableCell>
                              {l.reviewed_product ? (
                                <div className="flex flex-col gap-1" data-testid={`ddi-line-reviewed-product-${l.id}`}>
                                  <div className="flex items-center gap-1 flex-wrap">
                                    <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px]">
                                      <CheckCircle2 className="h-3 w-3 mr-1 inline" />{l.reviewed_product.product_name}
                                    </Badge>
                                    {isEditable && (
                                      <Button
                                        size="icon" variant="ghost" className="h-5 w-5" disabled={savingLineId === l.id}
                                        onClick={() => selectReviewedProduct(l.id, null)} data-testid={`ddi-clear-product-${l.id}`}
                                      >
                                        <X className="h-3 w-3" />
                                      </Button>
                                    )}
                                  </div>
                                  <ReviewKindTag kind={l.review_kind} testId={`ddi-review-kind-${l.id}`} />
                                </div>
                              ) : rememberedMatches[l.id] ? (
                                <div className="flex flex-col gap-1 max-w-[340px]" data-testid={`ddi-remembered-match-${l.id}`}>
                                  <IdentityMemoryCard
                                    memory={rememberedMatches[l.id]} canEdit={isEditable} testIdPrefix={`ddi-identity-${l.id}`}
                                    onChanged={(m) => setRememberedMatches((prev) => ({ ...prev, [l.id]: m }))}
                                  >
                                    {isEditable && (
                                      <Button
                                        size="sm" className="h-5 text-[10px]" disabled={savingLineId === l.id || (rememberedMatches[l.id].needs_review || []).length > 0}
                                        onClick={() => selectReviewedProduct(l.id, rememberedMatches[l.id].product.id, { mode: 'remembered', memoryId: rememberedMatches[l.id].memory_id })}
                                        data-testid={`ddi-use-remembered-${l.id}`}
                                      >
                                        {savingLineId === l.id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Use'}
                                      </Button>
                                    )}
                                  </IdentityMemoryCard>
                                  <Button
                                    size="sm" variant="outline" className="h-6 text-[10px] gap-1 self-start"
                                    onClick={() => toggleCandidates(l.id)} data-testid={`ddi-remembered-not-this-${l.id}`}
                                  >
                                    {rememberedMatches[l.id].status === 'active' ? 'Not this' : 'Suggest'}
                                    {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  size="sm" variant="outline" className="h-7 text-xs gap-1"
                                  onClick={() => toggleCandidates(l.id)}
                                  data-testid={`ddi-suggest-button-${l.id}`}
                                >
                                  <Lightbulb className="h-3 w-3" />
                                  Suggest
                                  {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                                </Button>
                              )}
                              <IdentityIssue
                                issue={identityIssues[l.id]} busy={savingLineId === l.id} testIdPrefix={`ddi-identity-${l.id}`}
                                onDismiss={() => dropIssue(l.id)}
                                onCorrect={() => selectReviewedProduct(l.id, identityIssues[l.id].productId, { mode: 'identity', correctMemory: true, reason: 'Corrected during delivery document review' })}
                              />
                            </TableCell>
                          </TableRow>
                          {expanded && (
                            <TableRow data-testid={`ddi-candidates-row-${l.id}`}>
                              <TableCell colSpan={12} className="bg-slate-50 p-0">
                                <div className="sticky left-0 max-w-4xl p-3 space-y-2" style={{ width: linesWidth || undefined }} data-testid={`ddi-candidates-panel-${l.id}`}>
                                {candState?.loading ? (
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Looking up Product Master candidates…</div>
                                ) : candState?.error ? (
                                  <div className="text-xs text-rose-700">{candState.error}</div>
                                ) : (candState?.candidates || []).length === 0 ? (
                                  <div className="text-xs text-muted-foreground">No Product Master candidates found for this line — informational only, nothing is linked automatically.</div>
                                ) : (
                                  <div className="space-y-1.5" data-testid={`ddi-candidates-list-${l.id}`}>
                                    <div className="text-xs text-muted-foreground mb-1">
                                      Ranked by name similarity, then evidence; an AI assessment is advisory. Conflicting candidates stay selectable. {isEditable ? '"Same product · remember" confirms an identity for this supplier/SKU/pack (eligibility-checked); "This line only" records a substitution or one-off and teaches nothing. Nothing is posted.' : 'Suggestions only.'}
                                    </div>
                                    <AiCompareStatus
                                      state={aiByKey[`rank-${l.id}`]}
                                      onRun={() => runAi(`rank-${l.id}`, l.id, candState.candidates.map((c) => c.product_id))}
                                      testIdPrefix={`ddi-ai-rank-${l.id}`}
                                    />
                                    {sortWithAi(candState.candidates, aiByKey[`rank-${l.id}`]?.data?.verdicts, 'product_id', (c) => c.conflict_tier).map((c) => (
                                      <div key={c.product_id} className="text-xs bg-white border rounded px-2 py-1.5" data-testid={`ddi-candidate-${l.id}-${c.product_id}`}>
                                        <div className="flex items-start justify-between gap-2">
                                          <div className="min-w-0">
                                            <div className="font-medium break-words">{c.product_name}{c.brand && <span className="text-muted-foreground font-normal"> ({c.brand})</span>}</div>
                                            <div className="flex items-center gap-2 flex-wrap text-[10px] text-muted-foreground mt-0.5">
                                              <Badge variant="outline" title="Name similarity — not a probability or identity confidence" className={`text-[10px] ${CANDIDATE_CLASS_STYLE[c.classification] || ''}`} data-testid={`ddi-candidate-score-${l.id}-${c.product_id}`}>
                                                Name similarity {Math.round(c.score * 100)}%
                                              </Badge>
                                              {CANDIDATE_CLASS_LABEL[c.classification] && (
                                                <span className="text-rose-700" data-testid={`ddi-candidate-conflict-${l.id}-${c.product_id}`}>{CANDIDATE_CLASS_LABEL[c.classification]}</span>
                                              )}
                                              {c.retrieved_by === 'shared_brand_token' && <span data-testid={`ddi-candidate-extra-${l.id}-${c.product_id}`}>found by shared brand word</span>}
                                              <span>Code: {c.product_code || '\u2014'}</span>
                                              <span>{c.subcategory || '\u2014'}</span>
                                              {c.status !== 'active' && <Badge variant="outline" className="text-[10px]">{c.status}</Badge>}
                                            </div>
                                          </div>
                                          {isEditable && (
                                            <ChoiceButtons
                                              busy={savingLineId === l.id} testIdPrefix={`ddi-select-candidate-${l.id}-${c.product_id}`}
                                              onIdentity={() => selectReviewedProduct(l.id, c.product_id, { mode: 'identity' })}
                                              onLineOnly={() => selectReviewedProduct(l.id, c.product_id, { mode: 'line_only' })}
                                            />
                                          )}
                                        </div>
                                        <AiVerdictChip verdict={aiByKey[`rank-${l.id}`]?.data?.verdicts?.[String(c.product_id)]} testId={`ddi-ai-verdict-${l.id}-${c.product_id}`} />
                                        <EvidencePillRow
                                          pills={pillsByLine[l.id]?.[c.product_id]}
                                          loading={pillsByLine[l.id]?.[c.product_id]?.loading}
                                          testIdPrefix={`ddi-pills-${l.id}-${c.product_id}`}
                                        />
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {/* Not limited to the ranked shortlist above — search the
                                    entire Product Master and explicitly pick an item that
                                    never appeared (or was outranked out of) that list. */}
                                <DeliveryLineFullMasterSearch
                                  lineId={l.id}
                                  isEditable={isEditable}
                                  savingLineId={savingLineId}
                                  onSelect={(productId, mode) => selectReviewedProduct(l.id, productId, { mode })}
                                  pillsForLine={pillsByLine[l.id] || {}}
                                  onLoadPills={(productIds) => loadPillsForLine(l.id, productIds)}
                                  aiState={aiByKey[`search-${l.id}`]}
                                  onRunAi={(ids) => runAi(`search-${l.id}`, l.id, ids)}
                                />

                                {/* Pass 6 — Research + Add to Product Master */}
                                {isEditable && !l.reviewed_product_id && (
                                  <div className="mt-3 border-t pt-3 space-y-2"
                                    data-testid={`dn-research-panel-${l.id}`}>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <Button
                                        type="button" size="sm" variant="outline"
                                        className="text-xs"
                                        disabled={lineResearch[l.id]?.loading}
                                        onClick={() => handleDnResearch(l.id, l.description_raw)}
                                        data-testid={`dn-research-btn-${l.id}`}>
                                        {lineResearch[l.id]?.loading
                                          ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" />Researching…</>
                                          : <><FlaskConical className="h-3 w-3 mr-1" />Research this product</>}
                                      </Button>
                                      <Button
                                        type="button" size="sm" variant="outline"
                                        className="text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                                        onClick={() => handleDnOpenAddProduct(l)}
                                        data-testid={`dn-add-product-btn-${l.id}`}>
                                        <Plus className="h-3 w-3 mr-1" />Add to Product Master
                                      </Button>
                                    </div>
                                    {lineResearch[l.id]?.error && (
                                      <div className="text-xs text-rose-600 flex items-center gap-1">
                                        <AlertTriangle className="h-3 w-3" />
                                        {lineResearch[l.id].error}
                                      </div>
                                    )}
                                    {renderResearchSections(lineResearch[l.id]?.sections)}
                                  </div>
                                )}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                          </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Pass 6 — DnAddProductModal: opened from Research panel; no auto-mapping */}
      {dnAddProductModal && (
        <DnAddProductModal
          line={dnAddProductModal.line}
          onCancel={() => setDnAddProductModal(null)}
          onProductCreated={(newProduct) => {
            setDnAddProductModal(null);
            toast({
              title: 'Product created',
              description: `${newProduct.product_name} added to Product Master. Search for it above to link to this line.`,
            });
          }}
        />
      )}
    </Card>
  );
}
